import {
	faFileAudio,
	faFilePdf,
	faImage,
	faPlay,
	faStop,
	faTrash,
	faVideo,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { tss } from 'tss-react';

import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';

import * as Document from '../types/Document';
import { Status } from '../types/Status';
import { Handlers } from '../useState';

interface Props {
	document: Document.Type;
	handlers: Handlers;
}

const DocumentListItem = ({ document, handlers }: Props) => {
	const { classes } = useStyles();
	const { t } = useTranslation();

	const getIcon = (type: string) => {
		switch (type) {
			case 'pdf':
				return faFilePdf;
			case 'image':
				return faImage;
			case 'video':
				return faVideo;
			case 'audio':
				return faFileAudio;
			default:
				return faFilePdf;
		}
	};

	return (
		<div className={classes.document} key={document.id}>
			<div className={classes.documentType}>
				<FontAwesomeIcon icon={getIcon(document.type)} size="1x" />
			</div>
			<div className={classes.documentInfo}>
				<div className={classes.documentInfoRow}>
					<div
						style={{ backgroundColor: Status.getStatusColor(document.status) }}
						className={classes.status}
					>
						{Status.getStatusText(document.status).toUpperCase()}
					</div>
					<div>{document.create_date}</div>
				</div>
				<div>{document.name}</div>
			</div>
			<RenderIf condition={document.status !== Status.Type.RUNNING}>
				<button
					className="btn btn-primary btn-sm"
					onClick={() => handlers.parseDocument(document)}
				>
					<FontAwesomeIcon icon={faPlay} className={classes.icon} />
					{t('admin:PARSING')}
				</button>
			</RenderIf>
			<RenderIf condition={document.status === Status.Type.RUNNING}>
				<button
					className="btn btn-secondary btn-sm"
					onClick={() => handlers.stopParsingDocument(document)}
				>
					<FontAwesomeIcon icon={faStop} className={classes.icon} />
					{t('admin:STOP_PARSING')}
				</button>
			</RenderIf>
			<button
				className="btn btn-danger btn-sm"
				onClick={() => handlers.deleteDocument(document)}
			>
				<FontAwesomeIcon icon={faTrash} className={classes.icon} />
				{t('_general:DELETE')}
			</button>
		</div>
	);
};

export default DocumentListItem;

export const useStyles = tss.create({
	container: {
		height: '100%',
		padding: '20px 20px',
		display: 'flex',
		flexDirection: 'column',
	},
	uploadContainer: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		border: '1px solid #ccc',
		padding: '10px',
		backgroundColor: '#f0f0f0',
		margin: '20px 0',
	},
	document: {
		fontSize: '16px',
		padding: '10px 20px',
		paddingRight: '10px',
		marginBottom: '-1px',
		display: 'flex',
		gap: '20px',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		border: '1px solid #ccc',
	},
	documentType: {
		fontSize: '16px',
		fontWeight: 'bold',
		flexGrow: 0,
	},
	documentInfo: {
		fontSize: '16px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		flexGrow: 1,
	},
	documentInfoRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: '5px',
		justifyContent: 'space-between',
		fontSize: '0.8em',
	},
	status: {
		padding: '2px 4px',
		fontSize: '0.7em',
		color: 'white',
	},
	icon: {
		marginRight: '5px',
	},
});
